'use client';

import ImageOptimizer from '@/components/image-optimizer';
import { PriceInfoData } from '@/data/academy.price.data';

const SUPPORT_BG_IMAGE = '/images/academy/price/price_bg.webp';

interface PriceInfoProps {
  data: PriceInfoData;
}

export default function PriceInfo({ data }: PriceInfoProps) {
  const {
    icon,
    courseName,
    conditionValue,
    tuition,
    tuitionNote,
    benefits,
    disclaimer,
  } = data;

  return (
    <div className="px-5 py-6 w-full flex flex-col items-center bg-white">
      <div className="max-w-[340px] w-full">
        {/* 과정 아이콘 & 이름 */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-[60px] h-[60px] rounded-full bg-[#F5F5FA] flex items-center justify-center mb-3">
            <ImageOptimizer
              src={icon}
              alt={courseName}
              width={36}
              height={36}
              className="object-contain"
            />
          </div>
          <span className="typo-20 font-bold text-222222">{courseName}</span>
          {conditionValue && (
            <span className="mt-2 typo-14 font-bold text-[#4F4F5E]">
              {conditionValue}
            </span>
          )}
        </div>

        {/* 수강료 카드 */}
        <div className="bg-[#F5F5FA] rounded-sm px-6 py-[18px] mb-3 flex items-center justify-between">
          <span className="typo-18 font-bold text-[#4F4F5E]">수강료</span>
          <span
            className="typo-20 font-extrabold text-222222"
            data-testid="license-tuition-amount"
          >
            {tuition != null && tuition > 0
              ? `${tuition.toLocaleString()}원`
              : '학원 문의'}
          </span>
        </div>
        {/* 금액의 기준 — 결제 요금제가 아닌 공개자료 금액은 조건 없이 인용되지 않게
            출처·분기·부가세를 바로 아래에 붙인다. */}
        {tuitionNote && (
          <p className="mb-3 typo-12 font-medium text-[#9E9E9E] leading-normal">
            {tuitionNote}
          </p>
        )}

        {benefits.length > 0 && (
          <>
            {/* 혜택 줄 — 쿠폰할인·지원금 …(api benefits 순서). 이 요금제를 운전면허플러스에서
                결제할 때 깎이는 금액(price_bg.webp, 320:46 비율). 혜택이 늘어도 같은 줄로 붙는다. */}
            <ul className="mb-5 flex flex-col gap-2">
              {benefits.map((b) => (
                <li
                  key={`${b.type}-${b.label}`}
                  className="px-6 py-4 flex items-center justify-between bg-contain bg-center bg-no-repeat w-full"
                  style={{
                    aspectRatio: '320 / 46',
                    backgroundImage: `url(${SUPPORT_BG_IMAGE})`,
                  }}
                  data-testid={`license-benefit-${b.type}`}
                >
                  <div className="flex items-center gap-1">
                    <ImageOptimizer
                      src="/images/academy/price/unit_won.svg"
                      alt="money"
                      width={16}
                      height={16}
                      className="object-contain"
                    />
                    <span className="typo-16 font-bold text-333333">
                      {b.label}
                    </span>
                  </div>
                  <span
                    className="typo-16 font-bold text-[#4F4F5E]"
                    data-testid={
                      b.type === 'coupon'
                        ? 'license-coupon-discount'
                        : `license-benefit-${b.type}-amount`
                    }
                  >
                    {b.amount.toLocaleString()}원 할인
                  </span>
                </li>
              ))}
            </ul>

            {/* 안내 문구 */}
            <p className="typo-12 font-medium text-[#9E9E9E] leading-normal">
              {disclaimer}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
